/**
 * CoachingTab — Broker-side chat supervision + coaching dashboard.
 *
 * Two sub-views toggled by pill buttons:
 *   1. "Ask the Broker" monitoring — read-only view of all agent broker-ai
 *      conversations via GET /chat/broker-monitoring, with ability to reply.
 *   2. "Team Chat" — broker can message agents directly via the existing
 *      POST /chat/team/chat endpoint.
 *
 * Reuses SplitPanel, SearchBar, ListItem, and the same chat bubble patterns
 * from InterofficeChatTab and AskTheBrokerTab.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faShieldAlt,
  faSpinner,
  faRobot,
  faPaperPlane,
  faCircle,
  faUsers,
  faPaperclip,
  faTimes,
  faImage,
  faFile,
  faDownload,
  faFilePdf,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../../helpers/api';
import { useAtom } from 'jotai';
import { user_info } from '../../../jotai/UserJotaiInstance';
import toast from 'react-hot-toast';
import { useSocket } from '../../../hooks/useSocket';
import SplitPanel from '../shared/SplitPanel';
import SearchBar from '../shared/SearchBar';
import ListItem from '../shared/ListItem';
import { colors, fonts } from '../shared/colors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MonitoredConversation {
  id: string;
  userId: string;
  agentName: string;
  agentEmail: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessage: string;
  lastMessageRole: string;
}

interface ChatMessage {
  id: string;
  content: string;
  role: string;
  senderName?: string;
  timestamp: Date;
  attachments?: any[];
}

interface TeamMember {
  id: string;
  collaborator_id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  isPending: boolean;
}

// Append auth token to attachment URLs
const getAuthUrl = (url: string) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('accessToken');
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CoachingTabProps {
  brokerageId?: string;
}

const CoachingTab: React.FC<CoachingTabProps> = () => {
  const [userInfo] = useAtom(user_info);

  // Sub-view toggle: 'monitoring' | 'team-chat'
  const [view, setView] = useState<'monitoring' | 'team-chat'>('monitoring');

  // --- Monitoring state ---
  const [monitoredConvs, setMonitoredConvs] = useState<MonitoredConversation[]>([]);
  const [loadingMonitored, setLoadingMonitored] = useState(false);
  const [monitoredSearch, setMonitoredSearch] = useState('');
  const [activeMonitored, setActiveMonitored] = useState<MonitoredConversation | null>(null);

  // --- Team chat state ---
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [activeTeamMember, setActiveTeamMember] = useState<TeamMember | null>(null);
  const [teamConversationId, setTeamConversationId] = useState<string | null>(null);

  // --- Shared chat state ---
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [fileAttachments, setFileAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Socket.io
  const { joinConversation, leaveConversation } = useSocket({
    userId: userInfo?.id?.toString(),
    onNewMessage: useCallback((message: any) => {
      if (message.metadata?.sender_id === userInfo?.id) return;
      const convId = view === 'monitoring' ? activeMonitored?.id : teamConversationId;
      if (convId && message.conversation_id === convId) {
        const newMsg: ChatMessage = {
          id: message.id,
          content: message.content,
          role: message.role || (message.metadata?.sender_id ? 'user' : 'assistant'),
          senderName: message.metadata?.sender_name,
          timestamp: new Date(message.timestamp),
          attachments: message.metadata?.attachments || [],
        };
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, newMsg];
        });
      }
    }, [view, activeMonitored?.id, teamConversationId, userInfo?.id]),
  });

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---------------------------------------------------------------------------
  // Monitoring: fetch conversations
  // ---------------------------------------------------------------------------

  const fetchMonitoredConversations = useCallback(async () => {
    setLoadingMonitored(true);
    try {
      const res = await axiosInstance.get('/chat/broker-monitoring');
      if (res.data.success) {
        setMonitoredConvs(res.data.conversations || []);
      }
    } catch (err) {
      console.error('Failed to fetch monitoring data:', err);
    } finally {
      setLoadingMonitored(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'monitoring') fetchMonitoredConversations();
  }, [view, fetchMonitoredConversations]);

  const filteredMonitored = useMemo(() => {
    if (!monitoredSearch) return monitoredConvs;
    const q = monitoredSearch.toLowerCase();
    return monitoredConvs.filter(
      (c) =>
        c.agentName.toLowerCase().includes(q) ||
        c.agentEmail.toLowerCase().includes(q) ||
        (c.title || '').toLowerCase().includes(q)
    );
  }, [monitoredConvs, monitoredSearch]);

  // Load messages for a monitored conversation
  const openMonitoredConversation = useCallback(async (conv: MonitoredConversation) => {
    // Leave previous room
    if (activeMonitored?.id) leaveConversation(activeMonitored.id);

    setActiveMonitored(conv);
    setMessages([]);
    setLoadingMessages(true);
    try {
      const res = await axiosInstance.get(`/chat/conversations/${conv.id}/messages`);
      const rawMsgs = res.data.messages || [];
      setMessages(
        rawMsgs.map((m: any) => ({
          id: m.id,
          content: m.content,
          role: m.role,
          senderName: m.metadata?.sender_name || (m.role === 'user' ? conv.agentName : 'Broker AI'),
          timestamp: new Date(m.timestamp),
          attachments: m.metadata?.attachments || [],
        }))
      );
      joinConversation(conv.id);
    } catch (err) {
      console.error('Failed to load conversation:', err);
      toast.error('Failed to load conversation');
    } finally {
      setLoadingMessages(false);
    }
  }, [activeMonitored?.id, joinConversation, leaveConversation]);

  // ---------------------------------------------------------------------------
  // Team chat: fetch members
  // ---------------------------------------------------------------------------

  const fetchTeamMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const brokerageRes = await axiosInstance.get('/chat/team/members');
      if (!brokerageRes.data.success || !brokerageRes.data.brokerage) return;
      const brokerage = brokerageRes.data.brokerage;
      const agentsRes = await axiosInstance.get(`/broker/agents/${brokerage.id}`);
      if (agentsRes.data.success) {
        const members: TeamMember[] = agentsRes.data.agents
          .filter((a: any) => a.user_id !== userInfo?.id?.toString())
          .map((agent: any) => {
            const nameClean = agent.agent_full_name?.replace(' (Pending Invitation)', '') || '';
            const parts = nameClean.split(' ');
            return {
              id: agent.id,
              collaborator_id: agent.user_id || agent.id,
              name: nameClean,
              firstName: parts[0] || '',
              lastName: parts.slice(1).join(' ') || '',
              email: agent.agent_email,
              role: agent.member_status,
              status: agent.isAuthenticated ? 'active' : 'pending',
              isPending: !agent.isAuthenticated,
            };
          });
        setTeamMembers(members);
      }
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, [userInfo?.id]);

  useEffect(() => {
    if (view === 'team-chat') fetchTeamMembers();
  }, [view, fetchTeamMembers]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch) return teamMembers;
    const q = memberSearch.toLowerCase();
    return teamMembers.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q)
    );
  }, [teamMembers, memberSearch]);

  // Start team chat with a member
  const startTeamChat = useCallback(async (member: TeamMember) => {
    if (teamConversationId) leaveConversation(teamConversationId);

    setActiveTeamMember(member);
    setMessages([]);
    setLoadingMessages(true);
    try {
      const createRes = await axiosInstance.post('/chat/conversations', {
        agent_type: 'human-collaborator',
        agent_id: member.collaborator_id,
        title: `Chat with ${member.name}`,
      });
      const convId = createRes.data.conversationId || createRes.data.conversation?.id;
      if (!convId) throw new Error('No conversation ID returned');

      setTeamConversationId(convId);
      joinConversation(convId);

      const messagesRes = await axiosInstance.get(`/chat/conversations/${convId}/messages`);
      if (messagesRes.data.success || messagesRes.data.messages) {
        setMessages(
          (messagesRes.data.messages || []).map((m: any) => ({
            id: m.id,
            content: m.content,
            role: m.metadata?.sender_id === userInfo?.id?.toString() ? 'user' : 'collaborator',
            senderName: m.metadata?.sender_name,
            timestamp: new Date(m.timestamp),
            attachments: m.metadata?.attachments || [],
          }))
        );
      }
    } catch (err) {
      console.error('Failed to start chat:', err);
      toast.error('Failed to open conversation');
    } finally {
      setLoadingMessages(false);
    }
  }, [teamConversationId, userInfo?.id, joinConversation, leaveConversation]);

  // ---------------------------------------------------------------------------
  // Send message (team chat + broker reply in monitored thread)
  // ---------------------------------------------------------------------------

  const handleSend = useCallback(async () => {
    const text = inputMessage.trim();
    if ((!text && fileAttachments.length === 0) || sending) return;

    const isTeamChat = view === 'team-chat';
    const convId = isTeamChat ? teamConversationId : activeMonitored?.id;
    const participantId = isTeamChat
      ? activeTeamMember?.collaborator_id
      : activeMonitored?.userId;

    if (!convId || !participantId) return;

    const tmpId = `tmp-${Date.now()}`;
    const senderName = `${userInfo?.user_first_name || ''} ${userInfo?.user_last_name || ''}`.trim();
    const userMsg: ChatMessage = {
      id: tmpId,
      content: text || `Sent ${fileAttachments.length} file(s)`,
      role: isTeamChat ? 'user' : 'broker',
      senderName,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    const filesToSend = [...fileAttachments];
    setFileAttachments([]);
    setSending(true);

    try {
      let response;

      if (!isTeamChat) {
        // Broker reply to monitored Ask the Broker conversation
        response = await axiosInstance.post('/chat/broker-reply', {
          conversation_id: convId,
          message: text,
        });
      } else if (filesToSend.length > 0) {
        const formData = new FormData();
        formData.append('conversation_id', convId);
        formData.append('message', text);
        formData.append('participant_id', participantId);
        filesToSend.forEach((file) => {
          if (file.type.startsWith('image/')) {
            formData.append('images', file);
          } else {
            formData.append('files', file);
          }
        });
        response = await axiosInstance.post('/chat/team/chat', formData);
      } else {
        response = await axiosInstance.post('/chat/team/chat', {
          conversation_id: convId,
          message: text,
          participant_id: participantId,
        });
      }

      if (response?.data.messageData) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tmpId
              ? { ...m, id: response.data.messageData.id, attachments: response.data.messageData.metadata?.attachments || [] }
              : m
          )
        );
      }
    } catch (err: any) {
      console.error('Send failed:', err);
      toast.error(err.response?.data?.error || 'Failed to send message');
      setMessages((prev) => prev.filter((m) => m.id !== tmpId));
      setInputMessage(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [inputMessage, fileAttachments, sending, view, teamConversationId, activeMonitored, activeTeamMember, userInfo]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFileAttachments((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setFileAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().substring(0, 2);

  const statusColor = (s: string) => {
    if (s === 'online' || s === 'active') return colors.green;
    if (s === 'away') return colors.orange;
    return colors.muted;
  };

  // Clear selections when switching views
  useEffect(() => {
    setMessages([]);
    setInputMessage('');
    setFileAttachments([]);
    if (view === 'monitoring') {
      setActiveTeamMember(null);
      if (teamConversationId) leaveConversation(teamConversationId);
      setTeamConversationId(null);
    } else {
      if (activeMonitored?.id) leaveConversation(activeMonitored.id);
      setActiveMonitored(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ---------------------------------------------------------------------------
  // Render: chat messages area (shared between both views)
  // ---------------------------------------------------------------------------

  const renderMessages = () => {
    const isMonitoringView = view === 'monitoring';

    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', minHeight: 0 }}>
        {loadingMessages ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: colors.muted }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} /> Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: colors.muted, marginTop: 60 }}>
            <FontAwesomeIcon icon={faComments} style={{ fontSize: 32, marginBottom: 8 }} />
            <div style={{ fontSize: 13 }}>No messages yet.</div>
          </div>
        ) : (
          <>
            {messages.map((msg) => {
              // Broker replies have role='broker', agent messages have role='user', AI has role='assistant'
              const isBroker = msg.role === 'broker';
              const isMe = isMonitoringView
                ? isBroker // in monitoring view, broker's own replies align right
                : msg.role === 'user';
              const isAI = msg.role === 'assistant';

              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: isMe ? 'flex-end' : 'flex-start',
                    marginBottom: 12,
                  }}
                >
                  {/* Avatar + label */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3,
                    flexDirection: isMe ? 'row-reverse' : 'row',
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%',
                      background: isAI
                        ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`
                        : isMe ? colors.navy3 : colors.navy2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: isAI ? colors.navy : isMe ? colors.muted : colors.gold,
                      fontSize: 9, fontWeight: 700,
                    }}>
                      {isAI ? (
                        <FontAwesomeIcon icon={faRobot} style={{ fontSize: 10 }} />
                      ) : (
                        getInitials(msg.senderName || 'U')
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: colors.muted, fontWeight: 600 }}>
                      {isAI ? 'Broker AI' : (msg.senderName || (isMe ? 'You' : 'Agent'))}
                    </span>
                    <span style={{ fontSize: 10, color: colors.muted }}>
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>

                  {/* Bubble */}
                  <div style={{
                    maxWidth: '70%',
                    background: isAI ? '#fff' : isMe ? colors.navy2 : '#fff',
                    color: isMe ? colors.white : colors.navy,
                    borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    overflow: 'hidden',
                    border: isMe ? 'none' : `1px solid ${colors.border}`,
                    boxShadow: isMe ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
                  }}>
                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (() => {
                      const images = msg.attachments.filter((a: any) => a.type === 'image' || a.mimetype?.startsWith('image/'));
                      const files = msg.attachments.filter((a: any) => !(a.type === 'image' || a.mimetype?.startsWith('image/')));
                      return (
                        <>
                          {images.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              {images.map((att: any, i: number) => {
                                const fileKey = att.s3Key || att.savedFileName || att.filename || '';
                                const fileName = att.name || att.originalName || 'Image';
                                const url = getAuthUrl(`/api/chat/attachments/${encodeURIComponent(fileKey)}`);
                                return (
                                  <a key={i} href={url} target="_blank" rel="noreferrer" style={{ display: 'block', lineHeight: 0 }}>
                                    <img src={url} alt={fileName} style={{ width: '100%', maxHeight: 280, objectFit: 'cover', display: 'block', cursor: 'pointer' }} />
                                  </a>
                                );
                              })}
                            </div>
                          )}
                          {files.length > 0 && (
                            <div style={{ padding: '8px 14px 4px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {files.map((att: any, i: number) => {
                                const fileKey = att.s3Key || att.savedFileName || att.filename || '';
                                const fileName = att.name || att.originalName || 'File';
                                const url = getAuthUrl(`/api/chat/attachments/${encodeURIComponent(fileKey)}`);
                                const isPdf = att.mimetype === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
                                const fileSize = att.size ? (att.size > 1048576 ? `${(att.size / 1048576).toFixed(1)} MB` : `${Math.round(att.size / 1024)} KB`) : '';
                                return (
                                  <a key={i} href={url} target="_blank" rel="noreferrer" style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '10px 12px',
                                    background: isMe ? 'rgba(255,255,255,0.08)' : '#f8f9fa',
                                    borderRadius: 8, textDecoration: 'none',
                                    border: isMe ? '1px solid rgba(255,255,255,0.1)' : `1px solid ${colors.border}`,
                                  }}>
                                    <div style={{
                                      width: 36, height: 36, borderRadius: 8,
                                      background: isPdf ? '#e74c3c' : colors.gold,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}>
                                      <FontAwesomeIcon icon={isPdf ? faFilePdf : faFile} style={{ color: '#fff', fontSize: 16 }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{
                                        fontSize: 13, fontWeight: 600,
                                        color: isMe ? '#fff' : colors.navy,
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                      }}>{fileName}</div>
                                      {fileSize && <div style={{ fontSize: 11, color: colors.muted, marginTop: 1 }}>{fileSize}</div>}
                                    </div>
                                    <FontAwesomeIcon icon={faDownload} style={{ color: isMe ? colors.gold : colors.muted, fontSize: 14, flexShrink: 0 }} />
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {/* Text */}
                    {msg.content && !(msg.attachments?.length > 0 && /^Sent \d+ file\(s\)$/.test(msg.content)) && (
                      <div style={{ padding: '10px 14px', fontSize: 14, lineHeight: 1.5, fontFamily: fonts.body, whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: input area (shared)
  // ---------------------------------------------------------------------------

  const renderInputArea = () => {
    const canSend = view === 'team-chat'
      ? !!activeTeamMember && !!teamConversationId
      : !!activeMonitored; // broker can reply in monitored threads

    if (!canSend) return null;

    const placeholderName = view === 'team-chat'
      ? activeTeamMember?.firstName || 'agent'
      : activeMonitored?.agentName?.split(' ')[0] || 'agent';

    return (
      <>
        {/* File attachment preview */}
        {fileAttachments.length > 0 && (
          <div style={{
            padding: '8px 24px', borderTop: `1px solid ${colors.border}`,
            background: colors.offBg, display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0,
          }}>
            {fileAttachments.map((file, i) => (
              <div key={i} style={{
                background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6,
                padding: '4px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <FontAwesomeIcon
                  icon={file.type.startsWith('image/') ? faImage : faFile}
                  style={{ color: colors.gold, fontSize: 10 }}
                />
                <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </span>
                <button
                  onClick={() => removeAttachment(i)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.muted, padding: 0 }}
                >
                  <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${colors.border}`, background: '#fff', flexShrink: 0 }}>
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-end',
            background: colors.offBg, borderRadius: 12,
            border: `1px solid ${colors.border}`, padding: '8px 12px',
          }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.muted, padding: '4px', flexShrink: 0 }}
              title="Attach file"
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </button>
            <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }} />
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              placeholder={`Reply to ${placeholderName}...`}
              rows={1}
              style={{
                flex: 1, border: 'none', background: 'transparent', resize: 'none', outline: 'none',
                fontSize: 14, fontFamily: fonts.body, color: colors.navy, lineHeight: 1.5, maxHeight: 120,
              }}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 120) + 'px';
              }}
            />
            <button
              onClick={handleSend}
              disabled={sending || (!inputMessage.trim() && fileAttachments.length === 0)}
              style={{
                background: (inputMessage.trim() || fileAttachments.length > 0)
                  ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})` : colors.border,
                border: 'none', borderRadius: 8, width: 38, height: 38,
                cursor: (inputMessage.trim() || fileAttachments.length > 0) ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s', flexShrink: 0,
              }}
            >
              <FontAwesomeIcon
                icon={sending ? faSpinner : faPaperPlane}
                spin={sending}
                style={{ color: (inputMessage.trim() || fileAttachments.length > 0) ? colors.navy : colors.muted, fontSize: 14 }}
              />
            </button>
          </div>
        </div>
      </>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: left panel content based on view
  // ---------------------------------------------------------------------------

  const renderLeftPanel = () => {
    if (view === 'monitoring') {
      return (
        <>
          {/* Header */}
          <div style={{
            padding: '16px', borderBottom: `1px solid ${colors.navy3}`,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <FontAwesomeIcon icon={faEye} style={{ color: colors.gold }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: 15, fontFamily: fonts.body }}>
              Agent Conversations
            </span>
            <span style={{
              background: colors.navy3, color: colors.muted, borderRadius: 10,
              padding: '2px 8px', fontSize: 11, fontWeight: 600, marginLeft: 'auto',
            }}>
              {monitoredConvs.length}
            </span>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <SearchBar value={monitoredSearch} onChange={setMonitoredSearch} placeholder="Search by agent or topic..." />
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingMonitored ? (
              <div style={{ padding: 32, textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </div>
            ) : filteredMonitored.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                <div style={{ fontSize: 13 }}>
                  {monitoredConvs.length === 0
                    ? 'No Ask the Broker conversations yet'
                    : 'No matching conversations'}
                </div>
              </div>
            ) : (
              filteredMonitored.map((conv) => (
                <ListItem
                  key={conv.id}
                  selected={activeMonitored?.id === conv.id}
                  onClick={() => openMonitoredConversation(conv)}
                  title={conv.agentName || 'Unknown Agent'}
                  subtitle={conv.title || 'Untitled conversation'}
                  meta={
                    <span>
                      {conv.messageCount} messages
                      <span style={{ margin: '0 6px' }}>·</span>
                      {formatTime(new Date(conv.updatedAt || conv.createdAt))}
                    </span>
                  }
                  rightContent={
                    conv.lastMessage ? (
                      <span style={{
                        fontSize: 10, color: colors.muted, maxWidth: 80,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                      }}>
                        {conv.lastMessageRole === 'assistant' ? 'AI: ' : ''}
                        {conv.lastMessage.substring(0, 40)}
                      </span>
                    ) : null
                  }
                />
              ))
            )}
          </div>
        </>
      );
    }

    // Team chat left panel
    return (
      <>
        <div style={{
          padding: '16px', borderBottom: `1px solid ${colors.navy3}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FontAwesomeIcon icon={faUsers} style={{ color: colors.gold }} />
          <span style={{ color: colors.white, fontWeight: 700, fontSize: 15, fontFamily: fonts.body }}>
            Team Members
          </span>
          <span style={{
            background: colors.navy3, color: colors.muted, borderRadius: 10,
            padding: '2px 8px', fontSize: 11, fontWeight: 600, marginLeft: 'auto',
          }}>
            {teamMembers.length}
          </span>
        </div>

        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.navy3}` }}>
          <SearchBar value={memberSearch} onChange={setMemberSearch} placeholder="Search team members..." />
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingMembers ? (
            <div style={{ padding: 32, textAlign: 'center', color: colors.muted }}>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
            </div>
          ) : filteredMembers.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
              <FontAwesomeIcon icon={faUsers} style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
              <div style={{ fontSize: 13 }}>
                {teamMembers.length === 0 ? 'No team members found' : 'No matching members'}
              </div>
            </div>
          ) : (
            filteredMembers.map((member) => (
              <ListItem
                key={member.id}
                selected={activeTeamMember?.id === member.id}
                onClick={() => !member.isPending && startTeamChat(member)}
                title={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {member.name}
                    <FontAwesomeIcon
                      icon={faCircle}
                      style={{ fontSize: 7, color: statusColor(member.status) }}
                    />
                  </span>
                }
                subtitle={member.email}
                meta={member.isPending ? 'Pending invitation' : member.role}
              />
            ))
          )}
        </div>
      </>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: right panel content
  // ---------------------------------------------------------------------------

  const renderRightPanel = () => {
    const hasSelection = view === 'monitoring' ? !!activeMonitored : !!activeTeamMember;

    if (!hasSelection) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', flex: 1, padding: 40, textAlign: 'center',
        }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 24, boxShadow: '0 4px 20px rgba(201,149,42,0.3)',
          }}>
            <FontAwesomeIcon
              icon={view === 'monitoring' ? faEye : faComments}
              style={{ fontSize: 36, color: colors.navy }}
            />
          </div>
          <h2 style={{ color: colors.navy, fontFamily: fonts.display, fontSize: 28, margin: '0 0 8px' }}>
            {view === 'monitoring' ? 'Agent Monitoring' : 'Team Chat'}
          </h2>
          <p style={{ color: colors.muted, fontSize: 14, maxWidth: 400, lineHeight: 1.6 }}>
            {view === 'monitoring'
              ? 'Select a conversation from the left to view your agents\' Ask the Broker questions and AI responses. You can reply directly to coach your agents.'
              : 'Select a team member from the left to start a direct conversation.'}
          </p>
        </div>
      );
    }

    const chatHeaderName = view === 'monitoring'
      ? activeMonitored?.agentName || 'Agent'
      : activeTeamMember?.name || 'Agent';
    const chatHeaderSub = view === 'monitoring'
      ? `Ask the Broker · ${activeMonitored?.title || ''}`
      : activeTeamMember?.email || '';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {/* Monitoring banner */}
        {view === 'monitoring' && (
          <div style={{
            padding: '6px 24px', background: colors.navy2,
            borderBottom: `1px solid ${colors.navy3}`,
            fontSize: 11, color: colors.gold, textAlign: 'center', fontFamily: fonts.body, flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={faEye} style={{ marginRight: 6, fontSize: 10 }} />
            Broker supervision view — you can reply to coach this agent
          </div>
        )}

        {/* Chat header */}
        <div style={{
          padding: '14px 24px', borderBottom: `1px solid ${colors.border}`,
          background: '#fff', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: colors.navy2, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: colors.gold, fontWeight: 700, fontSize: 14, fontFamily: fonts.body,
          }}>
            {getInitials(chatHeaderName)}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: colors.navy, fontFamily: fonts.body }}>
              {chatHeaderName}
            </div>
            <div style={{ fontSize: 11, color: colors.muted }}>
              {chatHeaderSub}
            </div>
          </div>
        </div>

        {/* Messages */}
        {renderMessages()}

        {/* Input area */}
        {renderInputArea()}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Sub-view toggle pills */}
      <div style={{
        padding: '12px 24px', background: colors.offBg,
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: colors.navy, marginRight: 8, fontFamily: fonts.body }}>
          View:
        </span>
        {[
          { key: 'monitoring' as const, label: 'Ask the Broker', icon: faShieldAlt },
          { key: 'team-chat' as const, label: 'Team Chat', icon: faComments },
        ].map((pill) => {
          const isActive = view === pill.key;
          return (
            <button
              key={pill.key}
              onClick={() => setView(pill.key)}
              style={{
                background: isActive
                  ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`
                  : '#fff',
                color: isActive ? colors.navy : colors.muted,
                border: isActive ? 'none' : `1px solid ${colors.border}`,
                borderRadius: 20,
                padding: '6px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: fonts.body,
                transition: 'all 0.15s',
              }}
            >
              <FontAwesomeIcon icon={pill.icon} style={{ fontSize: 12 }} />
              {pill.label}
            </button>
          );
        })}
        <span style={{ fontSize: 11, color: colors.muted, marginLeft: 'auto' }}>
          {view === 'monitoring'
            ? `${monitoredConvs.length} agent conversations`
            : `${teamMembers.length} team members`}
        </span>
      </div>

      {/* Split panel */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <SplitPanel
          leftWidth={340}
          left={renderLeftPanel()}
          right={renderRightPanel()}
        />
      </div>
    </div>
  );
};

export default CoachingTab;
